import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import Button from 'ext-ui/components/Button/Button'
import CodeInput from 'ext-ui/components/CodeInput/CodeInput'
import Modal from 'ext-ui/components/Modal/Modal'
import React, {
  FormEvent,
  ReactElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import type {
  BillPaymentDeliveryUpdate,
  BillPaymentReconciliationResult,
  BillPaymentSecondaryActionResult,
} from 'src/component-flyout/PayBillFlyout/useBillPaymentFlow'
import { useTranslate } from 'src/localization/Trns'
import { useScheduleBillPayment } from 'src/queries/payments/payments.queries'
import {
  BillPaymentAuthorizationChallengeResponse,
  BillPaymentDetailsResponse,
  BillPaymentError,
  BillPaymentResponse,
} from 'src/queries/payments/payments.types'
import {
  createBillPaymentIdempotencyKey,
  shouldReconcileBillPaymentError,
} from 'src/queries/payments/payments.utils'

import style from './MfaStepUpModal.style'

type PaymentIdentity = Pick<BillPaymentResponse, 'id' | 'revision'>

export type MfaStepUpModalProps = {
  open: boolean
  payment: PaymentIdentity
  authorization: BillPaymentAuthorizationChallengeResponse
  deliveryUpdate?: BillPaymentDeliveryUpdate
  onClose: () => void
  onResend: () => Promise<BillPaymentSecondaryActionResult>
  onRestart: () => Promise<BillPaymentSecondaryActionResult>
  onReconcileSchedule: (
    error: BillPaymentError,
  ) => Promise<BillPaymentReconciliationResult>
  onScheduled: (payment: BillPaymentResponse) => void
  onTerminalError: (error: BillPaymentError) => void
}

type ScheduleAttemptIdentity = {
  code: string
  idempotencyKey: string
}

const timestamp = (value: string): number => {
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? 0 : parsed
}

const formatRemaining = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60)
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`
}

export default function MfaStepUpModal(
  props: MfaStepUpModalProps,
): ReactElement {
  const {
    open,
    payment,
    authorization,
    deliveryUpdate,
    onClose,
    onResend,
    onRestart,
    onReconcileSchedule,
    onScheduled,
    onTerminalError,
  } = props
  const translate = useTranslate()
  const formId = `mfa-step-up-${useId()}`
  const [code, setCode] = useState('')
  const [codeError, setCodeError] = useState<string>()
  const [formError, setFormError] = useState<string>()
  const [isResending, setIsResending] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isReconciling, setIsReconciling] = useState(false)
  const [rejectedCode, setRejectedCode] = useState<string>()
  const [unknownOutcomeError, setUnknownOutcomeError] =
    useState<BillPaymentError>()
  const [retryAfterUntil, setRetryAfterUntil] = useState<number>()
  const [returnedResendAvailableAt, setReturnedResendAvailableAt] =
    useState<number>()
  const [currentTime, setCurrentTime] = useState(() => Date.now())
  const [deliveryAnnouncement, setDeliveryAnnouncement] = useState('')
  const formRef = useRef<HTMLFormElement>(null)
  const attemptRef = useRef<ScheduleAttemptIdentity | null>(null)
  const resendInFlightRef = useRef(false)
  const reportedTerminalRef = useRef<string>()
  const resendIntervalRef = useRef<number>()
  const expiryTimeoutRef = useRef<number>()
  const deliveryAnnouncementTimeoutRef = useRef<number>()
  const consumedDeliverySequenceRef = useRef<number>()
  const schedulePayment = useScheduleBillPayment()
  const sessionIdentity = JSON.stringify([
    payment.id,
    payment.revision,
    authorization.authorizationId,
  ])
  const activeSessionIdentityRef = useRef<string | null>(sessionIdentity)
  const sessionIdentityRef = useRef(sessionIdentity)
  activeSessionIdentityRef.current = sessionIdentity
  const authorizationResendAt = timestamp(authorization.resendAvailableAt)
  const effectiveResendAt = Math.max(
    authorizationResendAt,
    returnedResendAvailableAt ?? 0,
    retryAfterUntil ?? 0,
  )
  const resendSecondsRemaining = Math.max(
    0,
    Math.ceil((effectiveResendAt - currentTime) / 1_000),
  )
  const isResendBlocked = resendSecondsRemaining > 0
  const restartSecondsRemaining = Math.max(
    0,
    Math.ceil(((retryAfterUntil ?? 0) - currentTime) / 1_000),
  )
  const isRestartBlocked = restartSecondsRemaining > 0
  const expiresAt = timestamp(authorization.expiresAt)
  const isExpired = authorization.state === 'EXPIRED' || currentTime >= expiresAt
  const isAuthorizationTerminal =
    authorization.state === 'LOCKED' || authorization.attemptsRemaining <= 0
  const isDeliveryConfirmed = authorization.deliveryState === 'SENT'

  useEffect(() => {
    activeSessionIdentityRef.current = sessionIdentity
    return () => {
      if (activeSessionIdentityRef.current === sessionIdentity) {
        activeSessionIdentityRef.current = null
      }
    }
  }, [sessionIdentity])

  useEffect(() => {
    if (open) setCurrentTime(Date.now())
  }, [open])

  useEffect(() => {
    if (!open || effectiveResendAt <= Date.now()) return

    setCurrentTime(Date.now())
    const interval = window.setInterval(() => {
      const now = Date.now()
      setCurrentTime(now)
      if (now >= effectiveResendAt) {
        window.clearInterval(interval)
        resendIntervalRef.current = undefined
      }
    }, 1_000)
    resendIntervalRef.current = interval
    return () => {
      window.clearInterval(interval)
      if (resendIntervalRef.current === interval) {
        resendIntervalRef.current = undefined
      }
    }
  }, [effectiveResendAt, open])

  useEffect(() => {
    if (!open || isExpired || expiresAt <= Date.now()) return

    const timeout = window.setTimeout(
      () => setCurrentTime(Date.now()),
      expiresAt - Date.now(),
    )
    expiryTimeoutRef.current = timeout
    return () => {
      window.clearTimeout(timeout)
      if (expiryTimeoutRef.current === timeout) {
        expiryTimeoutRef.current = undefined
      }
    }
  }, [expiresAt, isExpired, open])

  useEffect(() => {
    if (sessionIdentityRef.current === sessionIdentity) return

    sessionIdentityRef.current = sessionIdentity
    setCurrentTime(Date.now())
    setCode('')
    setCodeError(undefined)
    setFormError(undefined)
    setIsResending(false)
    setIsSubmitting(false)
    setIsReconciling(false)
    setRejectedCode(undefined)
    setUnknownOutcomeError(undefined)
    setRetryAfterUntil(undefined)
    setReturnedResendAvailableAt(undefined)
    resendInFlightRef.current = false
    reportedTerminalRef.current = undefined
    attemptRef.current = null
    schedulePayment.reset()
  }, [schedulePayment.reset, sessionIdentity])

  const resetPrivateState = (): void => {
    setCode('')
    setCodeError(undefined)
    setFormError(undefined)
    setIsResending(false)
    setIsSubmitting(false)
    setIsReconciling(false)
    setRejectedCode(undefined)
    setUnknownOutcomeError(undefined)
    setRetryAfterUntil(undefined)
    setReturnedResendAvailableAt(undefined)
    resendInFlightRef.current = false
    attemptRef.current = null
    schedulePayment.reset()
  }

  const clearAttemptState = (): void => {
    setCode('')
    setCodeError(undefined)
    setFormError(undefined)
    setRejectedCode(undefined)
    setUnknownOutcomeError(undefined)
    attemptRef.current = null
    schedulePayment.reset()
  }

  const invalidCodeMessage = (attemptsRemaining?: number): string => {
    if (attemptsRemaining === undefined) {
      return translate(
        'The verification code is invalid. Edit the code before trying again.',
      )
    }
    if (attemptsRemaining === 1) {
      return translate(
        'The verification code is invalid. 1 attempt remaining.',
      )
    }
    return translate(
      'The verification code is invalid. ${attemptsRemaining} attempts remaining.',
      { attemptsRemaining: String(attemptsRemaining) },
    )
  }

  const terminalAuthorizationError = (): BillPaymentError => ({
    error: translate('The payment authorization is no longer available.'),
    errorCode: 'AUTHORIZATION_CODE_INVALID',
    attemptsRemaining: 0,
  })

  useEffect(() => {
    if (!open || !isAuthorizationTerminal) return

    const terminalIdentity = `${sessionIdentity}:${authorization.state}:${authorization.attemptsRemaining}`
    if (reportedTerminalRef.current === terminalIdentity) return

    reportedTerminalRef.current = terminalIdentity
    onTerminalError(terminalAuthorizationError())
  }, [
    authorization.attemptsRemaining,
    authorization.state,
    isAuthorizationTerminal,
    onTerminalError,
    open,
    sessionIdentity,
  ])

  const handleCodeChange = (nextCode: string): void => {
    if (nextCode !== code) {
      setCodeError(undefined)
      setFormError(undefined)
      setRejectedCode(undefined)
      attemptRef.current = null
    }
    setCode(nextCode)
  }

  const handleClose = (): void => {
    if (unknownOutcomeError || isReconciling) return
    if (resendIntervalRef.current !== undefined) {
      window.clearInterval(resendIntervalRef.current)
      resendIntervalRef.current = undefined
    }
    if (expiryTimeoutRef.current !== undefined) {
      window.clearTimeout(expiryTimeoutRef.current)
      expiryTimeoutRef.current = undefined
    }
    resetPrivateState()
    onClose()
  }

  const focusFirstDigit = (): void => {
    requestAnimationFrame(() => {
      formRef.current?.querySelector<HTMLInputElement>('input')?.focus()
    })
  }

  const captureReturnedResendWindow = (
    details: BillPaymentDetailsResponse,
  ): void => {
    const returnedAuthorization = details.authorization
    if (returnedAuthorization) {
      setReturnedResendAvailableAt(
        timestamp(returnedAuthorization.resendAvailableAt),
      )
    }
  }

  const handleSecondaryResult = (
    result: BillPaymentSecondaryActionResult,
  ): void => {
    if (result.kind === 'updated') {
      captureReturnedResendWindow(result.details)
      setRetryAfterUntil(undefined)
      clearAttemptState()
      focusFirstDigit()
      return
    }
    if (result.kind === 'expired') {
      clearAttemptState()
      return
    }
    if (result.kind === 'unchanged' || result.kind === 'unavailable') {
      setFormError(translate('We could not confirm a new code. Try again.'))
    }
  }

  const handleResend = async (): Promise<void> => {
    if (
      isSubmitting ||
      isResending ||
      isReconciling ||
      isResendBlocked ||
      unknownOutcomeError ||
      isAuthorizationTerminal ||
      resendInFlightRef.current
    ) {
      return
    }

    const resendSessionIdentity = sessionIdentity
    setFormError(undefined)
    setIsResending(true)
    resendInFlightRef.current = true
    try {
      const result = await onResend()
      if (activeSessionIdentityRef.current !== resendSessionIdentity) return

      handleSecondaryResult(result)
    } catch (caughtError) {
      if (activeSessionIdentityRef.current !== resendSessionIdentity) return

      const error = caughtError as BillPaymentError
      if (error.retryAfterSeconds && error.retryAfterSeconds > 0) {
        setRetryAfterUntil(Date.now() + error.retryAfterSeconds * 1_000)
      }
      setFormError(
        translate('We could not send a new code. Try again.'),
      )
    } finally {
      if (activeSessionIdentityRef.current === resendSessionIdentity) {
        resendInFlightRef.current = false
        setIsResending(false)
      }
    }
  }

  const handleRestart = async (): Promise<void> => {
    if (
      isSubmitting ||
      isResending ||
      isReconciling ||
      unknownOutcomeError ||
      isRestartBlocked
    ) {
      return
    }

    const restartSessionIdentity = sessionIdentity
    setFormError(undefined)
    setIsResending(true)
    try {
      const result = await onRestart()
      if (activeSessionIdentityRef.current !== restartSessionIdentity) return

      handleSecondaryResult(result)
    } catch (caughtError) {
      if (activeSessionIdentityRef.current !== restartSessionIdentity) return

      const error = caughtError as BillPaymentError
      if (error.retryAfterSeconds && error.retryAfterSeconds > 0) {
        setRetryAfterUntil(Date.now() + error.retryAfterSeconds * 1_000)
      }
      setFormError(translate('We could not send a new code. Try again.'))
    } finally {
      if (activeSessionIdentityRef.current === restartSessionIdentity) {
        setIsResending(false)
      }
    }
  }

  const reconcileSchedule = async (
    error: BillPaymentError,
    submittedSessionIdentity: string,
  ): Promise<void> => {
    setUnknownOutcomeError(error)
    setFormError(
      translate(
        'The payment outcome is not yet known. We’re checking the same request before you retry scheduling.',
      ),
    )
    setIsReconciling(true)

    let result: BillPaymentReconciliationResult
    try {
      result = await onReconcileSchedule(error)
    } catch {
      return
    } finally {
      if (activeSessionIdentityRef.current === submittedSessionIdentity) {
        setIsReconciling(false)
      }
    }

    if (activeSessionIdentityRef.current !== submittedSessionIdentity) return

    if (result.kind === 'unavailable') return
    const details = result.details

    if (details.payment.state === 'SCHEDULED') {
      resetPrivateState()
      onScheduled(details.payment)
      return
    }

    const reconciledAuthorization = details.authorization
    if (
      result.kind === 'authorization' &&
      reconciledAuthorization?.state === 'PENDING' &&
      result.sameAttempt
    ) {
      setUnknownOutcomeError(error)
      setFormError(
        translate(
          'The payment outcome is not yet known. We’re checking the same request before you retry scheduling.',
        ),
      )
      return
    }

    if (
      details.payment.state === 'AWAITING_AUTHORIZATION' &&
      reconciledAuthorization?.state === 'PENDING'
    ) {
      clearAttemptState()
      return
    }

    resetPrivateState()
    onTerminalError({
      error: translate('The payment authorization is no longer available.'),
      errorCode:
        reconciledAuthorization?.state === 'LOCKED'
          ? 'AUTHORIZATION_CODE_INVALID'
          : 'PAYMENT_STATE_CONFLICT',
      attemptsRemaining:
        reconciledAuthorization?.state === 'LOCKED' ? 0 : undefined,
    })
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (
      code.length !== 6 ||
      code === rejectedCode ||
      isSubmitting ||
      isResending ||
      isReconciling ||
      isExpired ||
      isAuthorizationTerminal ||
      !isDeliveryConfirmed
    ) {
      return
    }

    const attempt =
      attemptRef.current?.code === code
        ? attemptRef.current
        : { code, idempotencyKey: createBillPaymentIdempotencyKey() }
    attemptRef.current = attempt
    const submittedSessionIdentity = sessionIdentity
    setFormError(undefined)
    setIsSubmitting(true)

    let scheduledPayment: BillPaymentResponse
    try {
      scheduledPayment = await schedulePayment.mutateAsync({
        paymentId: payment.id,
        revision: payment.revision,
        authorizationId: authorization.authorizationId,
        ...attempt,
      })
    } catch (caughtError) {
      if (activeSessionIdentityRef.current !== submittedSessionIdentity) return

      const error = caughtError as BillPaymentError
      schedulePayment.reset()
      if (error.errorCode === 'AUTHORIZATION_CODE_INVALID') {
        if ((error.attemptsRemaining ?? 1) <= 0) {
          resetPrivateState()
          onTerminalError(error)
          return
        }
        setCodeError(invalidCodeMessage(error.attemptsRemaining))
        setRejectedCode(attempt.code)
        attemptRef.current = null
        return
      }

      if (error.errorCode === 'AUTHORIZATION_UNAVAILABLE') {
        clearAttemptState()
        setFormError(
          translate(
            "We couldn't verify that the code was delivered. Send a new code before trying again.",
          ),
        )
        return
      }

      if (shouldReconcileBillPaymentError(error)) {
        await reconcileSchedule(error, submittedSessionIdentity)
        return
      }

      resetPrivateState()
      onTerminalError(error)
      return
    } finally {
      if (activeSessionIdentityRef.current === submittedSessionIdentity) {
        setIsSubmitting(false)
      }
    }

    if (activeSessionIdentityRef.current !== submittedSessionIdentity) return

    resetPrivateState()
    onScheduled(scheduledPayment)
  }

  const isBusy =
    isSubmitting || schedulePayment.isPending || isResending || isReconciling
  const canSubmit =
    code.length === 6 &&
    code !== rejectedCode &&
    !isBusy &&
    !isExpired &&
    !isAuthorizationTerminal &&
    isDeliveryConfirmed
  const destinationHint = authorization.destinationHint?.trim()
  const deliveryCopy = useCallback(
    (
      deliveryState: BillPaymentAuthorizationChallengeResponse['deliveryState'],
      hint: string | null,
    ): string =>
      deliveryState === 'PENDING'
        ? translate('We’re confirming delivery of your six-digit code.')
        : deliveryState === 'FAILED'
          ? translate('We couldn’t send a verification code. Try again.')
          : hint
            ? translate('A six-digit code has been sent to ${destination}.', {
                destination: hint,
              })
            : translate('A six-digit code has been sent.'),
    [translate],
  )
  const deliveryDescription = deliveryCopy(
    authorization.deliveryState,
    destinationHint ?? null,
  )
  const deliveryUpdateSequence = deliveryUpdate?.sequence
  const deliveryUpdateState = deliveryUpdate?.deliveryState
  const deliveryUpdateHint = deliveryUpdate?.destinationHint

  useEffect(() => {
    window.clearTimeout(deliveryAnnouncementTimeoutRef.current)
    if (!open) {
      setDeliveryAnnouncement('')
      if (deliveryUpdateSequence !== undefined) {
        consumedDeliverySequenceRef.current = deliveryUpdateSequence
      }
      return
    }
    if (
      deliveryUpdateSequence === undefined ||
      deliveryUpdateState === undefined ||
      consumedDeliverySequenceRef.current === deliveryUpdateSequence
    ) {
      return
    }

    setDeliveryAnnouncement('')
    const announcement = deliveryCopy(
      deliveryUpdateState,
      deliveryUpdateHint?.trim() ?? null,
    )
    deliveryAnnouncementTimeoutRef.current = window.setTimeout(
      () => {
        consumedDeliverySequenceRef.current = deliveryUpdateSequence
        setDeliveryAnnouncement(announcement)
      },
      0,
    )
    return () => window.clearTimeout(deliveryAnnouncementTimeoutRef.current)
  }, [
    deliveryCopy,
    deliveryUpdateHint,
    deliveryUpdateSequence,
    deliveryUpdateState,
    open,
  ])
  const secondaryActionIsRestart = isExpired
  const secondaryActionDisabled =
    isBusy ||
    Boolean(unknownOutcomeError) ||
    isAuthorizationTerminal ||
    (secondaryActionIsRestart ? isRestartBlocked : isResendBlocked)
  const primaryLabel = unknownOutcomeError
    ? translate('Retry scheduling')
    : translate('Confirm and Schedule Payment')
  const secondaryActionSecondsRemaining = secondaryActionIsRestart
    ? restartSecondsRemaining
    : resendSecondsRemaining
  const secondaryActionBlocked = secondaryActionSecondsRemaining > 0
  const secondaryLabel = secondaryActionBlocked
    ? secondaryActionIsRestart
      ? translate('Send new code in ${remaining}', {
          remaining: formatRemaining(secondaryActionSecondsRemaining),
        })
      : translate('Resend code in ${remaining}', {
          remaining: formatRemaining(secondaryActionSecondsRemaining),
        })
    : secondaryActionIsRestart
      ? translate('Send new code')
      : translate('Resend code')

  const footer = (
    <div className="mfa-step-up-actions">
      <Button
        disabled={!canSubmit}
        form={formId}
        loading={isSubmitting || schedulePayment.isPending}
        type="submit"
      >
        {primaryLabel}
      </Button>
      <Button
        disabled={secondaryActionDisabled}
        loading={isResending}
        onClick={() =>
          void (secondaryActionIsRestart ? handleRestart() : handleResend())
        }
        type="button"
        variant="text"
      >
        {secondaryLabel}
      </Button>
    </div>
  )

  return (
    <Modal
      className="MfaStepUpModal"
      closable={!isBusy && !unknownOutcomeError}
      css={style}
      description={deliveryDescription}
      footer={footer}
      onClose={handleClose}
      open={open}
      title={translate("Verify it's you")}
    >
      <form
        className="mfa-step-up-form"
        id={formId}
        onSubmit={handleSubmit}
        ref={formRef}
      >
        <button hidden type="submit" />
        <CodeInput
          disabled={
            isBusy ||
            Boolean(unknownOutcomeError) ||
            isExpired ||
            isAuthorizationTerminal ||
            !isDeliveryConfirmed
          }
          errorText={codeError}
          label={translate('Verification Code')}
          onChange={handleCodeChange}
          value={code}
        />
        <div aria-atomic={'true'} aria-live={'polite'} role={'status'}>
          {deliveryAnnouncement}
        </div>
        {formError && (
          <div aria-label={formError} role="alert">
            <AlertPanel aria-hidden={true} variant="severe-wcag">
              {formError}
            </AlertPanel>
          </div>
        )}
      </form>
    </Modal>
  )
}
